import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  toast,
} from "@/components/ui/";
import { zodResolver } from "@hookform/resolvers/zod";
import { getRouteApi } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useLogin } from "./network/useLogin";

const EmailPasswordFormSchema = z.object({
  username: z.string().email("Please enter a valid email address").min(1, {
    message: "Email is required",
  }),
  password: z.string().min(1, "Password is required"),
});

type EmailPasswordFormValues = z.infer<typeof EmailPasswordFormSchema>;

export const EmailPasswordForm = () => {
  const routeApi = getRouteApi("/login");
  const { redirect } = routeApi.useSearch();
  const { mutate: login, isPending } = useLogin(redirect || "/");

  const form = useForm<EmailPasswordFormValues>({
    resolver: zodResolver(EmailPasswordFormSchema),
    mode: "onSubmit",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = (values: EmailPasswordFormValues) => {
    login(values, {
      onError: (error) => {
        const errorMessage =
          error.response?.data?.detail || "An error occurred during login";
        toast.error(errorMessage);
      },
    });
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-4">
      <Controller
        control={form.control}
        name="username"
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid}>
            <FieldLabel htmlFor="username">Username</FieldLabel>
            <Input {...field} id="username" aria-invalid={fieldState.invalid} />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />

      <Controller
        control={form.control}
        name="password"
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid}>
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Input
              {...field}
              id="password"
              type="password"
              aria-invalid={fieldState.invalid}
            />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <Button type="submit" disabled={isPending} className="w-full">
        {isPending ? "Logging in..." : "Login with Email"}
      </Button>
    </form>
  );
};
